// import { useEffect, useRef } from "react";
// import { io } from "socket.io-client";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   setNotifications,
//   setAllNotifications,
//   addNotification,
//   deleteNotification as deleteNotifFromStore,
//   setAdminNotifications,
//   addAdminNotification,
//   deleteAdminNotification,
// } from "../store/slices/notificationSlice";
// import { refreshIfNeeded } from "../store/actions/refreshIfNeeded";

// export default function useNotificationSocket() {
//   const dispatch = useDispatch();
//   const { user } = useSelector((s) => s.auth);

//   const role = localStorage.getItem("role");
//   const socketRef = useRef(null);
//   const adminSocketRef = useRef(null);

//   // ✅ Always pull latest access token
//   const getAccessToken = () => localStorage.getItem("accessToken");

//   const initSocket = (url) => {
//     const socket = io(url, {
//       autoConnect: false,          // don’t connect immediately
//       // reconnectionAttempts: 5,     // limit retries (avoid infinite spam)
//       // reconnectionDelay: 2000,     // wait 2s before retry
//       extraHeaders: { Authorization: `Bearer ${getAccessToken()}` },
//     });

//     // 🔄 Handle expired/missing tokens
//     socket.on("connect_error", async (err) => {
//       if (
//         err.message?.includes("Token has expired") ||
//         err.message?.includes("Authentication token not provided")
//       ) {
//         console.warn("⚠️ Socket error:", err.message);

//         // stop current connection attempts
//         socket.io.opts.reconnection = false;
//         socket.disconnect();

//         const refreshed = await dispatch(refreshIfNeeded(60));
//         if (refreshed) {
//           console.log("🔑 Token refreshed, reconnecting socket...");
//           socket.io.opts.extraHeaders = {
//             Authorization: `Bearer ${getAccessToken()}`,
//           };
//           socket.io.opts.reconnection = true;
//           socket.connect();
//         } else {
//           console.error("❌ Refresh failed, no reconnect.");
//         }
//       }
//     });

//     return socket;
//   };

//   // 🔔 User notifications
//   useEffect(() => {
//     if (!user?.id || !getAccessToken()) return;

//     socketRef.current = initSocket(
//       `${process.env.REACT_APP_SOCKET_SERVER_URL}/notifications/${user.id}/${role}`
//     );

//     const s = socketRef.current;
//     s.connect();

//     s.on("previous notification", (list) => {
//       dispatch(setNotifications(list));
//       dispatch(setAllNotifications(list));
//     });

//     s.on("notification", (notif) => {
//       if (role !== "ADMIN" && notif.user_id !== user.id) return;
//       dispatch(addNotification(notif));
//     });

//     s.on("deleteNotificationSuccess", ({ messageId }) =>
//       dispatch(deleteNotifFromStore(messageId))
//     );

//     return () => s.disconnect();
//   }, [user?.id, role, dispatch]);

//   // 🔔 Admin notifications
//   useEffect(() => {
//     if (role !== "ADMIN" || !getAccessToken()) return;

//     adminSocketRef.current = initSocket(
//       `${process.env.REACT_APP_SOCKET_SERVER_URL}/admin/notification`
//     );

//     const a = adminSocketRef.current;
//     a.connect();

//     a.on("previous-admin-notification", (list) =>
//       dispatch(setAdminNotifications(list))
//     );

//     a.on("admin-notification", (data) => {
//       console.log("🔔 Raw admin-notification received:", data);
//       const notif = data?.data ?? data;

//       dispatch((d, getState) => {
//         const state = getState();
//         console.log(
//           "📋 Current admin notifications:",
//           state.notification.adminNotifications
//         );

//         const exists = state.notification.adminNotifications.some(
//           (n) => n.id === notif.id
//         );

//         if (!exists) {
//           console.log("✅ Adding new admin notification:", notif);
//           d(addAdminNotification(notif));
//         } else {
//           console.log("⚠️ Duplicate notification ignored:", notif.id);
//         }
//       });
//     });

//     a.on("admin-notification-deleted", ({ success, id }) => {
//       if (success) dispatch(deleteAdminNotification(id));
//     });

//     return () => a.disconnect();
//   }, [role, dispatch]);

//   // ✅ Optional: background refresh to avoid expiry mid-session
//   useEffect(() => {
//     const interval = setInterval(() => {
//       dispatch(refreshIfNeeded(60)); // refresh 1 min before expiry
//     }, 4 * 60 * 1000); // every 4 minutes

//     return () => clearInterval(interval);
//   }, [dispatch]);

//   const emitDelete = (id, isAdmin = false) => {
//     if (isAdmin) {
//       adminSocketRef.current?.emit("delete-admin-notification", id);
//     } else {
//       socketRef.current?.emit("delete notification", { id });
//     }
//   };

//   return { emitDelete };
// }





 import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import {
  setNotifications,
  setAllNotifications,
  addNotification,
  deleteNotification as deleteNotifFromStore,
  setAdminNotifications,
  addAdminNotification,
  deleteAdminNotification,
} from "../store/slices/notificationSlice";

export default function useNotificationSocket() {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);

  const role = localStorage.getItem("role");
  const socketRef = useRef(null);
  const adminSocketRef = useRef(null);

  // ✅ Helper to get latest token from storage
  const getAccessToken = () => localStorage.getItem("accessToken");

  // ✅ Refresh token API call
  const refreshAccessToken = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include", // assuming refresh token is in cookie
      });
      if (!res.ok) throw new Error("Failed to refresh token");
      const data = await res.json();
      localStorage.setItem("accessToken", data.accessToken);
      return data.accessToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      return null;
    }
  };

  const initSocket = (url, isAdmin = false) => {
    const socket = io(url, {
      extraHeaders: { Authorization: `Bearer ${getAccessToken()}` },
    });

    // 🔄 Handle token expiration
    socket.on("connect_error", async (err) => {
      if (err.message?.includes("Token has expired")) {
        console.warn("Token expired — refreshing...");
        const newToken = await refreshAccessToken();
        if (newToken) {
          socket.io.opts.extraHeaders = { Authorization: `Bearer ${newToken}` };
          socket.connect();
        }
      }
    });

    return socket;
  };

  useEffect(() => {
    if (!user?.id  || !getAccessToken()) return;
 if (role === "ADMIN") return; // ✅ admins don't use this socket
    socketRef.current = initSocket(
      `${process.env.REACT_APP_SOCKET_SERVER_URL}/notifications/${user.id}/${role}`
    );

    const s = socketRef.current;

    s.on("previous notification", (list) => {
      dispatch(setNotifications(list));
      dispatch(setAllNotifications(list));
    });

    s.on("notification", (notif) => {
      if (role !== "ADMIN" && notif.user_id !== user.id) return;
      dispatch(addNotification(notif));
    });

    s.on("deleteNotificationSuccess", ({ messageId }) =>
      dispatch(deleteNotifFromStore(messageId))
    );

    return () => s.disconnect();
  }, [user?.id, role, dispatch]);

  useEffect(() => {
    if (role !== "ADMIN" || !getAccessToken()) return;

    adminSocketRef.current = initSocket(
      `${process.env.REACT_APP_SOCKET_SERVER_URL}/admin/notification`,
      true
    );

    const a = adminSocketRef.current;

    a.on("previous-admin-notification", (list) =>
      dispatch(setAdminNotifications(list))
    );

    a.on("admin-notification", (data) => {
      const notif = data?.data ?? data;
      dispatch((d, getState) => {
        const exists = getState().notification.adminNotifications.some(
          (n) => n.id === notif.id
        );
        if (!exists) d(addAdminNotification(notif));
      });
    });

    a.on("admin-notification-deleted", ({ success, id }) => {
      if (success) dispatch(deleteAdminNotification(id));
    });

    return () => a.disconnect();
  }, [role, dispatch]);

  const emitDelete = (id, isAdmin = false) => {
    if (isAdmin) {
      adminSocketRef.current?.emit("delete-admin-notification", id);
    } else {
      socketRef.current?.emit("delete notification", { id });
    }
  };

  return { emitDelete };
}